import { useMutation, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useContext, useState } from 'react'

import { ConfirmButton } from '@/components/ConfirmButton'
import { Button } from '@/components/elements/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/elements/tooltip'
import { createDocument } from '@/services/documents.service'
import { updateEvent } from '@/services/events.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatTimeAndDate } from '@/utils/formatters'
import { toast } from '@/utils/helpers'

import FileUploader from '../FileUploader/FileUploader'

/**
 * RosterUpload component for uploading event rosters
 * Shows upload interface if no roster exists, or success state with Replace/Remove
 * controls if a roster is uploaded. Once any registered certificate has been issued,
 * the roster can only be replaced — not removed — so the audit trail is preserved.
 */
export const RosterUpload = ({ event }) => {
  const queryClient = useQueryClient()
  const { user } = useContext(UserStoreContext)
  const [replacing, setReplacing] = useState(false)

  const hasIssuedCerts = (event.studentCertificates || []).some((sc) => sc.status === 'Issued')

  const { mutate: uploadRoster, isPending: isUploading } = useMutation({
    mutationFn: async (fileInfo) => {
      const document = await createDocument({
        name: fileInfo.fileName || 'Event Roster',
        type: 'Roster',
        filePath: fileInfo.fileUploadName,
        entity: event.entity,
        owner: user.id,
      })
      return updateEvent({ id: event.id, roster: document.id })
    },
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast(replacing ? 'Roster replaced successfully' : 'Roster uploaded successfully', 'success')
      setReplacing(false)
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })

  const { mutate: removeRoster, isPending: isRemoving } = useMutation({
    mutationFn: () => updateEvent({ id: event.id, roster: null }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast('Roster removed', 'success')
      queryClient.invalidateQueries({ queryKey: ['events'] })
    },
  })

  if (event.roster && !replacing) {
    const removeButton = (
      <ConfirmButton
        confirmTitle="Remove Roster"
        confirmContent={
          'Are you sure you want to remove this roster? Certificates cannot be granted ' +
          'for this event until a new roster is uploaded.'
        }
        variant="destructive"
        size="sm"
        loading={isRemoving}
        disabled={hasIssuedCerts}
        onClick={() => removeRoster()}
      >
        Remove
      </ConfirmButton>
    )

    return (
      <div className="border-success bg-success/10 flex flex-col gap-2 rounded-md border p-4">
        <h4 className="text-success font-semibold">Roster Uploaded</h4>
        <p className="text-sm">
          <strong>File:</strong> {event.roster.name}
        </p>
        {event.roster.createdAt && (
          <p className="text-sm">
            <strong>Uploaded:</strong> {formatTimeAndDate(event.roster.createdAt)}
          </p>
        )}
        {event.roster.signedFilePath && (
          <a
            href={event.roster.signedFilePath}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary text-sm underline hover:no-underline"
          >
            Download Roster
          </a>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setReplacing(true)}>
            Replace
          </Button>
          {hasIssuedCerts ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{removeButton}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                Certificates have already been issued for this event. The roster can be replaced
                but not removed.
              </TooltipContent>
            </Tooltip>
          ) : (
            removeButton
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="border-warning bg-warning/10 flex flex-col gap-3 rounded-md border p-4">
      <h4 className="text-warning font-semibold">
        {replacing ? 'Replace Roster' : 'Roster Required'}
      </h4>
      <p className="text-sm">
        {replacing
          ? 'Upload a new file to replace the existing roster.'
          : 'A roster must be uploaded before certificates can be granted to students for this event.'}
      </p>
      <div className="mt-2">
        <FileUploader
          filePrefix="rosters"
          handleUploadToServer={(fileInfo) => uploadRoster(fileInfo)}
          disabled={isUploading}
        />
      </div>
      {replacing && (
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setReplacing(false)}
            disabled={isUploading}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}

RosterUpload.propTypes = {
  event: PropTypes.object.isRequired,
}

export default RosterUpload
